import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { get, hash, array } from '@ember/helper';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsButton,
  HdsTable,
  HdsLinkInline,
  HdsBadge,
  HdsIcon,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  FlexGrid,
  Flex,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import {
  optionForResourceService,
  resourceTypeFor,
  optionForSourceState,
} from 'core/helpers';
import { TYPE_NETWORK } from 'common/utils/cloud-resource-types';
import LearnMore from 'hcp/components/page/hvns/cards/learn-more';
import ZeroState from 'hcp/components/page/hvns/list/zero-state';

/**
 *
 * `model` is an array of HVNs.
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnsList` renders the HashiCorp Virtual Networks List page.
 *
 *
 * ```
 * <Page::Hvns::List
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageHvnsList
 *
 */
export default class PageHvnsList extends Component {
  @service analytics;

  get resourceType() {
    return TYPE_NETWORK;
  }

  // TODO: [TCE-2007] Remove HVN activation A/B test analytics instrumentation.
  captureAnalyticsEventABTestHvnCreateIntentHeaderAction = () => {
    const abTestVariation = variation('hcp-ui-ab-test-hvn-activation-create');

    this.analytics.trackEvent(
      'external_ui_hvn_activation_create_intent_button_clicked',
      { abTestVariation, buttonLocation: 'list-page-header-action' }
    );
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title>
            {{t "components.page.hvns.list.title"}}
          </PH.Title>
          {{#if (can "create network" @model.networks)}}
            <PH.Actions>
              <HdsButton
                @color="primary"
                @icon="plus"
                @iconPosition="leading"
                @route="cloud.orgs.detail.projects.detail.hvns.create"
                @text={{t "components.page.hvns.create.action"}}
                data-test-network-create
                {{on
                  "click"
                  this.captureAnalyticsEventABTestHvnCreateIntentHeaderAction
                }}
              />
            </PH.Actions>
          {{/if}}
        </HdsPageHeader>
      </:header>
      <:alert>
        <LimitBanner
          @variant={{this.resourceType}}
          @count={{@model.networks.length}}
          @padding="0"
        />
      </:alert>
      <:body>
        {{#if @model.networks.length}}
          <HdsTable
            @caption={{get
              (optionForResourceService (hash type=(resourceTypeFor "network")))
              "name"
            }}
            @model={{@model.networks}}
            @columns={{array
              (hash label=(t "components.page.hvns.list.table.headers.name"))
              (hash label=(t "components.page.hvns.list.table.headers.status"))
              (hash
                label=(t "components.page.hvns.list.table.headers.provider")
              )
              (hash label=(t "components.page.hvns.list.table.headers.region"))
              (hash
                label=(t "components.page.hvns.list.table.headers.cidr-block")
              )
            }}
            @valign="middle"
            {{boxMargin "0 0 lg 0"}}
            data-test-hvns-list-table
          >
            <:body as |B|>
              <B.Tr data-test-hvns-list-table-item>
                <B.Td>
                  <HdsLinkInline
                    @color="secondary"
                    @model={{B.data.id}}
                    @route="cloud.orgs.detail.projects.detail.hvns.detail"
                    class="hds-font-weight-semibold"
                    data-test-hvn-detail-link
                  >
                    {{B.data.id}}
                  </HdsLinkInline>
                </B.Td>
                <B.Td data-test-hvns-list-table-item-state>
                  {{#let (optionForSourceState "hvn" B.data.state) as |option|}}
                    <HdsBadge
                      @text={{t option.text}}
                      @color={{option.color}}
                      @icon={{option.icon}}
                      @isIconOnly={{option.isIconOnly}}
                      @size={{option.size}}
                      @type={{option.type}}
                    />
                  {{/let}}
                </B.Td>
                <B.Td data-test-hvns-list-table-item-provider>
                  {{#let
                    (optionForSourceState
                      "region" B.data.location.region.provider
                    )
                    as |option|
                  }}
                    <Flex @alignItems="center" @gap="2xs">
                      <HdsIcon @name={{option.icon}} @isInline={{true}} />
                      <span
                        class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                      >
                        {{t option.text}}
                      </span>
                    </Flex>
                  {{/let}}
                </B.Td>
                <B.Td data-test-hvns-list-table-item-region>
                  {{B.data.location.region.region}}
                </B.Td>
                <B.Td data-test-hvns-list-table-item-cidr>
                  {{B.data.cidrBlock}}
                </B.Td>
              </B.Tr>
            </:body>
          </HdsTable>
          <div>
            <FlexGrid class="hvn-page__width-constrained" as |G|>
              <G.Item @xs="12" @sm="12" @md="4" @lg="4" @xl="4">
                <div {{boxPadding "0 0 lg 0"}}>
                  <LearnMore />
                </div>
              </G.Item>
            </FlexGrid>
          </div>
        {{else}}
          <ZeroState @model={{@model}} />
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
